import { useColorModeValue } from "@chakra-ui/react";
import { ClerkProvider } from "@clerk/clerk-react";
import { Navigate, Outlet, Route, Routes } from "react-router";

import AdminIndex from "@/admin";
import RequireReflagAdmin from "@/admin/components/RequireReflagAdmin";
import AdminLinearCleanupIssue from "@/admin/pages/AdminLinearCleanupIssue";
import AdminOrg from "@/admin/pages/AdminOrg";
import AdminPullRequest from "@/admin/pages/AdminPullRequest";
import AppLayout from "@/app/components/AppLayout";
import { FeaturesToFlagsRedirect } from "@/app/components/FeaturesToFlagsRedirect";
import { BannerProvider } from "@/app/components/TopBanner";
import ChooseOrgForIntegration from "@/app/pages/ChooseOrgForIntegration";
import Debugger from "@/app/pages/Debugger";
import DebuggerDistinct from "@/app/pages/DebuggerDistinct";
import DebuggerProblems from "@/app/pages/DebuggerProblems";
import DebuggerRecent from "@/app/pages/DebuggerRecent";
import GenerateDemoApp from "@/app/pages/GenerateDemoApp";
import GetStarted from "@/app/pages/GetStarted";
import Invite from "@/app/pages/Invite";
import NewApp from "@/app/pages/NewApp";
import Onboarding from "@/app/pages/Onboarding";
import { reflagDark, reflagLight } from "@/app/styles/clerk";
import GoToDefaultEnv from "@/auth/components/GoToDefaultEnv";
import GoToHome from "@/auth/components/GoToHome";
import OrgSuspended from "@/auth/components/OrgSuspended";
import RequireBootstrapped from "@/auth/components/RequireBoostrapped";
import RequireEnvironment from "@/auth/components/RequireEnvironment";
import Auth from "@/auth/pages/Auth";
import {
  LoginError as CLILoginError,
  LoginSuccess as CLILoginSuccess,
} from "@/auth/pages/CLILogin";
import { AIChat } from "@/common/components/AIChat";
import { IntercomIdentity } from "@/common/components/IntercomIdentity";
import { EnvironmentProvider } from "@/common/contexts/EnvironmentContext";
import { ReflagProvider } from "@/common/providers/ReflagProvider";
import { API_URL, CLERK_PUBLISHABLE_KEY } from "@/common/utils/env";
import CompaniesToSegmentsRedirect from "@/company/components/CompaniesToSegmentsRedirect";
import Company from "@/company/pages/Company";
import { CompanyAttributes } from "@/company/pages/CompanyAttributes";
import CompanyFeatures from "@/company/pages/CompanyFeatures";
import CompanyFeedback from "@/company/pages/CompanyFeedback";
import CompanyUsers from "@/company/pages/CompanyUsers";
import Segment from "@/company/pages/Segment";
import { DragPreviewLayer } from "@/feature/components/DragPreviewLayer";
import GoToFlagKey from "@/feature/components/GoToFlagKey";
import { OrphanFeatureDropTarget } from "@/feature/components/OrphanFeatureDropTarget";
import Feature from "@/feature/pages/Feature";
import FeatureConfigs from "@/feature/pages/FeatureConfigs";
import FeatureMonitor from "@/feature/pages/FeatureMonitor";
import FeatureOptIn from "@/feature/pages/FeatureOptIn";
import Features from "@/feature/pages/Features";
import FeatureSettings from "@/feature/pages/FeatureSettings";
import FeatureTargeting from "@/feature/pages/FeatureTargeting";
import NewFeature from "@/feature/pages/NewFeature";
import Feedback from "@/feedback/pages/Feedback";
import GlobalSettings from "@/global-settings/pages/GlobalSettings";
import User from "@/user/pages/User";
import { UserAttributes } from "@/user/pages/UserAttributes";
import UserFlags from "@/user/pages/UserFlags";

import InvalidRoute from "../pages/InvalidRoute";
import { InitLayout } from "./InitLayout";

import "@/app/styles/app.css";

function AppRoutes() {
  const clerkTheme = useColorModeValue(reflagLight, reflagDark);

  const apiDomain = new URL(API_URL).origin;
  return (
    <ClerkProvider
      afterSignOutUrl="/"
      allowedRedirectOrigins={[apiDomain]}
      appearance={{
        baseTheme: clerkTheme,
      }}
      publishableKey={CLERK_PUBLISHABLE_KEY}
    >
      <Routes>
        <Route element={<Auth mode="login" />} path="login" />
        <Route element={<Auth mode="signup" />} path="signup" />
        <Route element={<CLILoginSuccess />} path="cli-login/success" />
        <Route element={<CLILoginError />} path="cli-login/error" />
        <Route
          element={
            <EnvironmentProvider>
              <IntercomIdentity />
              <BannerProvider>
                <ReflagProvider>
                  <RequireBootstrapped>
                    <DragPreviewLayer />
                    <Outlet />
                  </RequireBootstrapped>
                </ReflagProvider>
              </BannerProvider>
            </EnvironmentProvider>
          }
        >
          <Route element={<GoToHome />} path="/" />
          <Route element={<OrgSuspended />} path="org-suspended" />
          <Route element={<RequireReflagAdmin />} path="admin">
            <Route element={<AdminIndex />} index />
            <Route element={<AdminOrg />} path=":orgSlug" />
            <Route
              element={<AdminPullRequest />}
              path=":orgSlug/pulls/:pullId"
            />
            <Route
              element={<AdminLinearCleanupIssue />}
              path=":orgSlug/linear-cleanup-issues/:issueId"
            />
          </Route>

          <Route
            element={
              <InitLayout>
                <Outlet />
              </InitLayout>
            }
            path=""
          >
            <Route
              element={<Invite />}
              path="/orgs/:orgSlug/invite/:inviteKey"
            />
            <Route element={<GetStarted />} path="get-started" />
            <Route element={<ChooseOrgForIntegration />} path="choose-org" />
            <Route element={<NewApp />} path="apps/new" />
          </Route>

          <Route
            element={<GenerateDemoApp />}
            path="/orgs/:orgSlug/gen-demo-app"
          />

          <Route element={<GoToDefaultEnv />} path="env-current/*" />

          <Route
            element={
              <RequireEnvironment>
                <AppLayout>
                  <Outlet />
                  <OrphanFeatureDropTarget />
                  <AIChat />
                </AppLayout>
              </RequireEnvironment>
            }
            path="envs/:envSlug"
          >
            <Route element={<Navigate to="flags" replace />} path="" />

            <Route element={<Features />} path="flags">
              <Route element={<NewFeature />} path="new" />
            </Route>

            <Route element={<GoToFlagKey />} path="flags/by-key/:flagKey/*" />

            <Route element={<Feature />} path="flags/:featureSlug">
              <Route element={<Navigate to="access" replace />} path="" />
              <Route element={<FeatureTargeting />} path="access" />
              <Route element={<FeatureOptIn />} path="opt-in" />
              <Route element={<FeatureConfigs />} path="config" />
              <Route element={<FeatureMonitor />} path="monitor" />
              <Route path="settings">
                <Route element={<Navigate to="general" replace />} path="" />
                <Route element={<FeatureSettings />} path=":section" />
              </Route>
            </Route>

            {/* Redirects for legacy routes */}
            <Route element={<FeaturesToFlagsRedirect />} path="features/*" />
            {/* end */}

            <Route path="settings">
              <Route element={<Navigate to="org-general" replace />} path="" />
              <Route element={<GlobalSettings />} path=":section" />
            </Route>

            <Route element={<Segment />} path="segments/:segmentSlug?" />
            {/* Legacy redirect from companies to segments */}
            <Route element={<CompaniesToSegmentsRedirect />} path="companies" />
            <Route element={<Company />} path="companies/:companyId">
              <Route element={<Navigate to="flags" replace />} path="" />
              <Route element={<CompanyFeatures />} path="flags" />
              <Route element={<CompanyFeedback />} path="feedback" />
              <Route element={<CompanyUsers />} path="users" />
              <Route element={<CompanyAttributes />} path="attributes" />
              {/* Redirects for legacy routes */}
              <Route
                element={<Navigate to="../flags" replace />}
                path="features"
              />
              {/* end */}
            </Route>

            <Route element={<User />} path="users/:userId">
              <Route element={<Navigate to="flags" replace />} path="" />
              <Route element={<UserFlags />} path="flags" />
              <Route element={<UserAttributes />} path="attributes" />
            </Route>

            <Route element={<Debugger />} path="event-log">
              <Route element={<Navigate to="recent" replace />} path="" />
              <Route element={<DebuggerRecent />} path="recent" />
              <Route element={<DebuggerProblems />} path="problems" />
              <Route element={<DebuggerDistinct />} path="distinct" />
            </Route>
            <Route element={<Feedback />} path="feedback" />
            <Route element={<Onboarding />} path="onboarding" />
          </Route>
        </Route>
        <Route element={<InvalidRoute />} path="*" />
      </Routes>
    </ClerkProvider>
  );
}

export default AppRoutes;
